import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FolderOpen, Upload } from 'lucide-react';
import {
  canManageDocuments,
  canViewAllDocuments,
  visibleDocumentCategories,
  type DocumentCategory,
  type DocumentResponse,
  type DocumentsSummaryResponse,
} from '@timesheet/schemas';
import { PageTransition } from '../components/PageTransition';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DocumentFilters, type DocumentFiltersState, type PersonOption } from '../components/documents/DocumentFilters';
import { DocumentList } from '../components/documents/DocumentList';
import { DocumentDetailDialog } from '../components/documents/DocumentDetailDialog';
import { DocumentEditDialog } from '../components/documents/DocumentEditDialog';
import { ConfirmDeleteDocumentDialog } from '../components/documents/ConfirmDeleteDocumentDialog';
import { DocumentUploadSheet } from '../components/documents/DocumentUploadSheet';
import { PayslipUploadSheet } from '../components/documents/payslip-upload/PayslipUploadSheet';
import { categoryLabel, ctaLabel, defaultTab, isDocumentCategory } from '../components/documents/documents.utils';
import { useAuth, UserRole } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useApprovalScope } from '../hooks/use-approval-scope';
import { documentsService } from '../services/documentsService';
import { userService, type ApiUser } from '../services/userService';
import { getApiBaseUrl } from '../lib/api-base-url';
import { BlobDownloadError, openBlobInNewTab } from '../lib/download-blob';

const TABS_CLASS = 'scrollbar-hide h-auto w-full justify-start gap-0 overflow-x-auto rounded-none border-b bg-transparent p-0 sm:gap-1';
const TAB_CLASS =
  'rounded-none border-b-2 border-transparent px-3 py-2 data-[state=active]:border-orange-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none';

/** One page for every role: the tabs are exactly the categories the role may see. */
const Documents: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();

  const role = user?.role;
  const tabs = useMemo(() => visibleDocumentCategories(role), [role]);
  const tabParam = searchParams.get('tab');
  const activeTab: DocumentCategory = isDocumentCategory(tabParam) && tabs.includes(tabParam) ? tabParam : defaultTab(role);
  const canManageTab = canManageDocuments(role, activeTab);
  const seesAllPayslips = canViewAllDocuments(role, 'BUSTE_PAGA');
  const seesAllContracts = canViewAllDocuments(role, 'CONTRATTI');
  const isManager = role === UserRole.MANAGER;
  const scope = useApprovalScope({ namespace: 'documents', defaultOnlyMine: false, enabled: seesAllContracts || isManager });

  const [summary, setSummary] = useState<DocumentsSummaryResponse | null>(null);
  const [documents, setDocuments] = useState<DocumentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<DocumentFiltersState>({ search: '' });
  const [search, setSearch] = useState('');
  const [tenantUsers, setTenantUsers] = useState<ApiUser[]>([]);
  const [selected, setSelected] = useState<DocumentResponse | null>(null);
  const [dialog, setDialog] = useState<'detail' | 'edit' | 'delete' | 'upload' | 'payslips' | null>(null);
  const [downloading, setDownloading] = useState(false);
  const handledDeepLink = useRef<string | null>(null);

  const setTab = useCallback(
    (tab: DocumentCategory) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', tab);
      next.delete('doc');
      setSearchParams(next, { replace: true });
      setFilters({ search: '' });
      setSearch('');
    },
    [searchParams, setSearchParams],
  );

  // Search is debounced; the other filters apply immediately.
  useEffect(() => {
    const handle = window.setTimeout(() => setFilters((prev) => (prev.search === search ? prev : { ...prev, search })), 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  const fetchSummary = useCallback(async () => {
    try {
      setSummary(await documentsService.summary());
    } catch (error) {
      console.error('Error fetching documents summary:', error);
    }
  }, []);

  const contractsNeedScope = activeTab === 'CONTRATTI' && (seesAllContracts || isManager);
  const fetchList = useCallback(async () => {
    if (contractsNeedScope && !scope.ready) return;
    try {
      setLoading(true);
      setDocuments(
        await documentsService.list({
          category: activeTab,
          userId: filters.userId,
          year: filters.year,
          month: filters.month,
          payslipType: filters.payslipType,
          search: filters.search,
          onlyMine: activeTab === 'CONTRATTI' && seesAllContracts && scope.onlyMine,
        }),
      );
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters, contractsNeedScope, scope.ready, scope.onlyMine, seesAllContracts]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Person filter data: the tenant list only for roles allowed to call GET /users.
  useEffect(() => {
    if (!seesAllPayslips) return;
    userService
      .getAll({ includeInactive: true })
      .then(setTenantUsers)
      .catch((error) => console.error('Error fetching users:', error));
  }, [seesAllPayslips]);

  // Deep link from notifications and mails: ?doc=<id>
  useEffect(() => {
    const docParam = searchParams.get('doc');
    if (!docParam || handledDeepLink.current === docParam) return;
    handledDeepLink.current = docParam;
    documentsService
      .get(Number(docParam))
      .then((doc) => {
        if (!doc) {
          showNotification(t('documents.errors.DOCUMENT_NOT_FOUND', 'Documento non trovato o non più disponibile'), 'error');
          const next = new URLSearchParams(searchParams);
          next.delete('doc');
          setSearchParams(next, { replace: true });
          return;
        }
        if (doc.category !== activeTab && tabs.includes(doc.category)) {
          const next = new URLSearchParams(searchParams);
          next.set('tab', doc.category);
          setSearchParams(next, { replace: true });
        }
        setSelected(doc);
        setDialog('detail');
      })
      .catch((error) => console.error('Error opening document link:', error));
  }, [searchParams, setSearchParams, activeTab, tabs, showNotification, t]);

  const refresh = useCallback(async () => {
    await Promise.all([fetchList(), fetchSummary()]);
  }, [fetchList, fetchSummary]);

  const handleDownload = async (doc: DocumentResponse, mode: 'save' | 'open' = 'save') => {
    try {
      setDownloading(true);
      if (mode === 'open') await openBlobInNewTab(`${getApiBaseUrl()}/documents/${doc.id}/download`);
      else await documentsService.download(doc);
    } catch (error) {
      if (error instanceof BlobDownloadError) {
        showNotification(t(`documents.errors.${error.code}`, t('documents.errors.download', 'Download non riuscito')), 'error');
      } else {
        console.error('Error downloading document:', error);
      }
    } finally {
      setDownloading(false);
    }
  };

  const openDetail = (doc: DocumentResponse) => {
    setSelected(doc);
    setDialog('detail');
  };

  const closeDialog = () => setDialog(null);

  const people: PersonOption[] | undefined = useMemo(() => {
    if (activeTab === 'BUSTE_PAGA' && seesAllPayslips) return tenantUsers.map((u) => ({ id: u.id, name: u.name, isActive: u.isActive }));
    if (activeTab === 'CONTRATTI' && seesAllContracts) return tenantUsers.map((u) => ({ id: u.id, name: u.name, isActive: u.isActive }));
    if (activeTab === 'CONTRATTI' && isManager) return scope.directReports.map((u) => ({ id: u.id, name: u.name }));
    return undefined;
  }, [activeTab, seesAllPayslips, seesAllContracts, isManager, tenantUsers, scope.directReports]);

  const showPerson = activeTab === 'CONTRATTI' || (activeTab === 'BUSTE_PAGA' && seesAllPayslips);
  const hasFilters = !!(filters.search || filters.userId || filters.year || filters.month || filters.payslipType);
  const hideContractsPill = activeTab === 'CONTRATTI' && seesAllContracts && scope.onlyMine;

  return (
    <PageTransition className="p-4 sm:p-6 space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400">
            <FolderOpen className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('documents.title', 'Documenti')}</h1>
            <p className="text-sm text-muted-foreground">{t('documents.subtitle', 'Buste paga, contratti e documenti aziendali')}</p>
          </div>
        </div>
        {canManageTab && (
          <Button className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600" onClick={() => setDialog(activeTab === 'BUSTE_PAGA' ? 'payslips' : 'upload')}>
            <Upload className="mr-2 h-4 w-4" />
            {ctaLabel(activeTab, t)}
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setTab(value as DocumentCategory)}>
        <TabsList className={TABS_CLASS}>
          {tabs.map((tab) => (
            <TabsTrigger key={tab} value={tab} className={TAB_CLASS} data-testid={`documents-tab-${tab}`}>
              {categoryLabel(tab, t)}
              {summary && !(tab === 'CONTRATTI' && hideContractsPill) && (
                <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums text-muted-foreground">{summary.counts[tab]}</span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <DocumentFilters
        category={activeTab}
        filters={filters}
        onChange={(next) => {
          setFilters(next);
          setSearch(next.search);
        }}
        years={summary?.payslipYears ?? []}
        people={people}
        currentUserId={user?.id}
        scope={activeTab === 'CONTRATTI' && seesAllContracts ? scope : undefined}
      />

      <DocumentList
        category={activeTab}
        documents={documents}
        loading={loading || (contractsNeedScope && !scope.ready)}
        showPerson={showPerson}
        canManage={canManageTab}
        hasFilters={hasFilters}
        managerWithoutReports={activeTab === 'CONTRATTI' && isManager && scope.ready && scope.directReports.length === 0}
        onOpen={openDetail}
        onDownload={(doc) => handleDownload(doc)}
        onUpload={() => setDialog(activeTab === 'BUSTE_PAGA' ? 'payslips' : 'upload')}
      />

      <DocumentDetailDialog
        doc={selected}
        open={dialog === 'detail'}
        downloading={downloading}
        onClose={closeDialog}
        onDownload={(doc) => handleDownload(doc)}
        onOpenInTab={(doc) => handleDownload(doc, 'open')}
        onEdit={() => setDialog('edit')}
        onDelete={() => setDialog('delete')}
      />
      <DocumentEditDialog
        doc={selected}
        open={dialog === 'edit'}
        onClose={() => setDialog('detail')}
        onSaved={(doc) => {
          setSelected(doc);
          setDialog('detail');
          refresh();
        }}
      />
      <ConfirmDeleteDocumentDialog
        doc={selected}
        open={dialog === 'delete'}
        onClose={() => setDialog('detail')}
        onDeleted={() => {
          setSelected(null);
          closeDialog();
          refresh();
        }}
      />
      {activeTab !== 'BUSTE_PAGA' && (
        <DocumentUploadSheet
          open={dialog === 'upload'}
          category={activeTab}
          people={people ?? []}
          onClose={closeDialog}
          onUploaded={() => {
            closeDialog();
            refresh();
          }}
        />
      )}
      <PayslipUploadSheet
        open={dialog === 'payslips'}
        people={tenantUsers}
        onClose={closeDialog}
        onDone={() => {
          closeDialog();
          refresh();
        }}
      />
    </PageTransition>
  );
};

export default Documents;
